// Página "Ayuda" — FAQ y soporte para clientes
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SUPPORT_EMAIL } from '@/config/constants';
import {
  ChevronDownIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface FAQItem {
  questionKey: string;
  answerKey: string;
}

const FAQ_KEYS: FAQItem[] = [
  { questionKey: 'help.faqUploadDocs', answerKey: 'help.faqUploadDocsAnswer' },
  { questionKey: 'help.faqStatuses', answerKey: 'help.faqStatusesAnswer' },
  { questionKey: 'help.faqReviewTime', answerKey: 'help.faqReviewTimeAnswer' },
  { questionKey: 'help.faqRejected', answerKey: 'help.faqRejectedAnswer' },
  { questionKey: 'help.faqInvoices', answerKey: 'help.faqInvoicesAnswer' },
  { questionKey: 'help.faqAppointment', answerKey: 'help.faqAppointmentAnswer' },
  { questionKey: 'help.faqDuration', answerKey: 'help.faqDurationAnswer' },
  { questionKey: 'help.faqDocuments', answerKey: 'help.faqDocumentsAnswer' },
];

function AccordionItem({ questionKey, answerKey, t, defaultOpen }: {
  questionKey: string;
  answerKey: string;
  t: (key: string) => string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen || false);
  const answerId = `faq-answer-${questionKey}`;

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-4 text-left"
        aria-expanded={open}
        aria-controls={answerId}
      >
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 pr-4">
          {t(questionKey)}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <p id={answerId} className="text-sm text-gray-600 dark:text-gray-400 pb-4 pr-8">
          {t(answerKey)}
        </p>
      )}
    </div>
  );
}

export function ClientesAyuda() {
  const { t } = useTranslation('portal');
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrar FAQ por búsqueda
  const filteredFAQ = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_KEYS;
    const q = searchQuery.toLowerCase();
    return FAQ_KEYS.filter(
      (item) =>
        t(item.questionKey).toLowerCase().includes(q) ||
        t(item.answerKey).toLowerCase().includes(q)
    );
  }, [searchQuery, t]);

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('help.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('help.subtitle')}
          </p>
        </div>

        {/* FAQ */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('help.faq')}
            </h3>

            {/* Búsqueda */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
              <Input
                type="search"
                placeholder={t('help.searchPlaceholder', 'Buscar en preguntas frecuentes...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label={t('help.searchLabel', 'Buscar en preguntas frecuentes')}
              />
            </div>

            <div>
              {filteredFAQ.length > 0 ? (
                filteredFAQ.map((item) => (
                  <AccordionItem
                    key={item.questionKey}
                    questionKey={item.questionKey}
                    answerKey={item.answerKey}
                    t={t}
                    defaultOpen={searchQuery.length > 0}
                  />
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                  {t('help.noResults', 'No se encontraron resultados para tu búsqueda.')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t('help.notFound')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('help.supportAvailable')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <EnvelopeIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('help.email')}</p>
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-primary hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <PhoneIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('help.phone')}</p>
                  <a href="tel:+34910000000" className="text-sm text-primary hover:underline">
                    +34 910 000 000
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <ClockIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('help.schedule')}</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{t('help.scheduleValue')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
